// Optimized tracking system with batching, caching, and analytics
class OptimizedTracker {
  constructor() {
    this.batchQueue = [];
    this.batchTimeout = null;
    this.sessionCache = new Map();
    this.trackingCache = new Map();
    this.BATCH_SIZE = 5;
    this.BATCH_DELAY = 2000; // 2 seconds
    this.CACHE_DURATION = 300000; // 5 minutes
  }

  // Get or create session ID with caching
  getSessionId(contentId, contentType) {
    const sessionKey = `${contentType.toLowerCase()}Session_${contentId}`;
    let sessionId = sessionStorage.getItem(sessionKey);
    
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(sessionKey, sessionId);
    }
    
    return sessionId;
  }

  // Check if interaction should be tracked (debouncing)
  shouldTrack(userId, contentId, action, sessionId) {
    const cacheKey = `${userId || 'anon'}_${contentId}_${action}_${sessionId}`;
    const now = Date.now();
    
    if (this.trackingCache.has(cacheKey)) {
      const lastTracked = this.trackingCache.get(cacheKey);
      if (now - lastTracked < 5000) { // 5 second debounce
        return false;
      }
    }
    
    this.trackingCache.set(cacheKey, now);
    
    // Clean old cache entries
    if (this.trackingCache.size > 1000) {
      const cutoff = now - this.CACHE_DURATION;
      for (const [key, timestamp] of this.trackingCache.entries()) {
        if (timestamp < cutoff) {
          this.trackingCache.delete(key);
        }
      }
    }
    
    return true;
  }

  // Add interaction to batch queue
  async trackInteraction(contentId, contentType, action, user, metadata = {}) {
    const sessionId = this.getSessionId(contentId, contentType);
    const userId = user?.id;
    
    // Check debouncing
    if (!this.shouldTrack(userId, contentId, action, sessionId)) {
      return null;
    }

    const interactionData = {
      [contentType === 'MOVIE' ? 'movieID' : 'showID']: contentId,
      contentType,
      action,
      sessionID: sessionId,
      deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
      platform: 'web',
      metadata: JSON.stringify({
        ...metadata,
        timestamp: Date.now(),
        url: window.location.pathname,
        referrer: document.referrer || 'direct'
      }),
      isAnonymous: !user
    };

    if (userId) {
      interactionData.userID = userId;
    }

    // Add to batch queue
    this.batchQueue.push(interactionData);
    
    // Process batch if full or start timer
    if (this.batchQueue.length >= this.BATCH_SIZE) {
      await this.processBatch();
    } else if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => this.processBatch(), this.BATCH_DELAY);
    }

    return { queued: true, batchSize: this.batchQueue.length };
  }

  // Process batch of interactions
  async processBatch() {
    if (this.batchQueue.length === 0) return;

    const batch = [...this.batchQueue];
    this.batchQueue = [];
    
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    try {
      // Import trackInteraction dynamically to avoid circular imports
      const { trackInteraction } = await import('./hypeRatingClient');
      
      const results = await Promise.allSettled(
        batch.map(interaction => trackInteraction(interaction, !interaction.isAnonymous))
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      console.log(`📊 Batch tracking completed: ${successful} successful, ${failed} failed`);
      
      // Note: HypeRating recalculation is handled by trackInteraction Lambda
      
      // Re-queue failed interactions (up to 3 retries)
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const interaction = batch[index];
          interaction.retryCount = (interaction.retryCount || 0) + 1;
          
          if (interaction.retryCount <= 3) {
            this.batchQueue.push(interaction);
          }
        }
      });

    } catch (error) {
      console.error('Batch processing failed:', error);
      // Re-queue all interactions for retry
      this.batchQueue.unshift(...batch);
    }
  }

  // Track page view with enhanced metadata
  async trackPageView(contentId, contentType, user, metadata = {}) {
    const enhancedMetadata = {
      ...metadata,
      source: 'page_load',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      userAgent: navigator.userAgent.substring(0, 200) // Truncate for storage
    };

    return this.trackInteraction(contentId, contentType, 'view', user, enhancedMetadata);
  }

  // Track engagement with duration and scroll depth
  async trackEngagement(contentId, contentType, user, duration, scrollDepth = 0) {
    const engagementLevel = duration > 300 ? 'high' : duration > 60 ? 'medium' : 'low';
    
    return this.trackInteraction(contentId, contentType, 'view_duration', user, {
      duration,
      scrollDepth,
      engagementLevel,
      source: 'engagement_tracking'
    });
  }

  // Get session analytics
  getSessionAnalytics() {
    const sessionData = {};
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes('Session_')) {
        const value = sessionStorage.getItem(key);
        sessionData[key] = value;
      }
    }

    return {
      activeSessions: Object.keys(sessionData).length,
      queueSize: this.batchQueue.length,
      cacheSize: this.trackingCache.size,
      sessions: sessionData
    };
  }

  // Force process remaining batch (for page unload)
  async flush() {
    if (this.batchQueue.length > 0) {
      await this.processBatch();
    }
  }
}

// Create singleton instance
export const optimizedTracker = new OptimizedTracker();

// Enhanced tracking functions
export const trackPageView = (contentId, contentType, user, metadata) => 
  optimizedTracker.trackPageView(contentId, contentType, user, metadata);

export const trackEngagement = (contentId, contentType, user, duration, scrollDepth) => 
  optimizedTracker.trackEngagement(contentId, contentType, user, duration, scrollDepth);

export const trackAction = (contentId, contentType, action, user, metadata) => 
  optimizedTracker.trackInteraction(contentId, contentType, action, user, metadata);

export const getTrackingAnalytics = () => optimizedTracker.getSessionAnalytics();

export const flushTracking = () => optimizedTracker.flush();